/// <reference types="google.maps" />
import { envPublic } from '@/libs/config/env.public';

import { withGeocodeCache } from './geocode-cache';
import type { Coords } from './use-current-location';

/**
 * Web forward-geocoder. expo-location's `geocodeAsync` throws in the browser
 * ("Geocoder service is not available"), so we use the Maps JavaScript API's `Geocoder`
 * instead — it's CORS-safe and reuses the API already loaded by the map's <APIProvider>
 * on the find-academy screen.
 *
 * Requires the **Geocoding API** enabled on GOOGLE_MAP_API_KEY (same key the web map uses).
 */

/** Poll for the Maps JS bootstrap in case a search fires before the map finishes loading. */
async function waitForMapsApi(): Promise<typeof google.maps | null> {
  for (let attempt = 0; attempt < 50; attempt++) {
    if (typeof google !== 'undefined' && typeof google.maps?.importLibrary === 'function') {
      return google.maps;
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  return null;
}

async function resolveViaMapsJs(query: string): Promise<Coords | null> {
  // No key → the map (and thus the Geocoder) never loads; nothing to resolve against.
  if (!envPublic.googleMapsApiKey) return null;

  const maps = await waitForMapsApi();
  if (!maps) return null;

  try {
    const { Geocoder } = (await maps.importLibrary('geocoding')) as google.maps.GeocodingLibrary;
    const { results } = await new Geocoder().geocode({ address: query });
    const location = results[0]?.geometry.location;
    if (!location) return null;
    return { latitude: location.lat(), longitude: location.lng() };
  } catch {
    // No result, over quota, or the Geocoding API isn't enabled on the key.
    return null;
  }
}

/**
 * Web forward-geocoder, cached per query (see {@link withGeocodeCache}) so repeated searches
 * don't re-hit the paid API.
 */
export const geocodeAddress = withGeocodeCache(resolveViaMapsJs);
